// Our data store
import reduxStore from '../store';
import axios from 'axios';
import { setAlert } from './alert/actions';
import { isOrderLoadedInState, clearStoredOrderId } from '../utils/orderRouteUtils';
import {
    isAnonymousAuthRequest,
    redirectToLoginAfterAuthFailure,
} from '../utils/authSessionUtils';
import { readHttpErrorStatus } from '../utils/httpErrorUtils';

const {dispatch} = reduxStore;

const UNAUTHORIZED = 401;
const FORBIDDEN = 403;
const ORDER_MISSING_ENDPOINTS = [
    'Order/GetOrder',
    'Order/GetOrderRevision',
    'Order/GetOrderRevisionLog',
];

const readRequestOrderId = (config: { params?: Record<string, unknown> } | undefined): number | null => {
    if (!config || !config.params) {
        return null;
    }

    const raw = config.params.apporderid != null
        ? config.params.apporderid
        : config.params.appOrderId;

    if (raw == null) {
        return null;
    }

    const parsed = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
    return Number.isNaN(parsed) ? null : parsed;
};

const maybeHandleUnauthorized = (response: { status?: number; config?: { url?: string } }) => {
    const status = response.status;
    if (status !== UNAUTHORIZED && status !== FORBIDDEN) {
        return;
    }
    const url = (response.config && response.config.url) || '';
    if (isAnonymousAuthRequest(url)) {
        return;
    }
    dispatch({ type: 'USER_IS_VALID_ERROR', isValid: false });
    redirectToLoginAfterAuthFailure();
};

const maybeNotifyDeletedActiveOrder = (response: { status?: number; config?: { url?: string; params?: Record<string, unknown> } }) => {
    if (response.status !== 404) {
        return;
    }

    const url = response.config && response.config.url ? response.config.url : '';
    if (!ORDER_MISSING_ENDPOINTS.some((endpoint) => url.indexOf(endpoint) !== -1)) {
        return;
    }

    const requestOrderId = readRequestOrderId(response.config);
    const storedOrderId = localStorage.getItem('order');
    const activeOrderId = storedOrderId ? parseInt(storedOrderId, 10) : requestOrderId;

    if (activeOrderId == null || Number.isNaN(activeOrderId)) {
        return;
    }

    if (requestOrderId != null && requestOrderId !== activeOrderId) {
        return;
    }

    const orderState = reduxStore.getState().order.order;
    if (!isOrderLoadedInState(orderState, activeOrderId)) {
        clearStoredOrderId();
        return;
    }

    dispatch({
        type: 'REMOTE_ORDER_UNAVAILABLE',
        payload: 'deleted',
    });
};

// Add a response interceptor
axios.interceptors.response.use(function (response) {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    if (response.status === UNAUTHORIZED || response.status === FORBIDDEN) {
        maybeHandleUnauthorized(response);
        return Promise.reject(response);
    }

    if (response.status > 399) {
        maybeNotifyDeletedActiveOrder(response);

        if (response.status > 499) {
            const message = response.data.error || '';
            dispatch(setAlert(message, 'critical'))
        }
        return Promise.reject(response);
    }

    return response;
  }, function (error) {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error
    if (readHttpErrorStatus(error) != null) {
        const response = (error && error.response) ? error.response : error;
        maybeHandleUnauthorized(response);
        maybeNotifyDeletedActiveOrder(response);
    }

    return Promise.reject(error);
  });

axios.interceptors.request.use(config => {
    config['validateStatus'] = function (status) {
        return status >= 200 && status < 550; // default
    };
    return config;
})

export default axios;   